import { useCallback, useEffect, useState } from 'react';
/* INTEZ TELEPHONY OVERVIEW CLEANUP v15.6.1 */
import {
  CheckCircle2,
  Clock3,
  Headphones,
  Link2,
  Phone,
  PhoneCall,
  PhoneMissed,
  RefreshCw,
  Users,
  X,
} from 'lucide-react';
import { api } from '../../api';
import { useAuth } from '../../auth';
import { userHasRole } from '../../roles';

export type WorkplaceDialTarget = {
  phone: string;
  contactId?: string;
  companyId?: string;
  ticketId?: string;
  contactName?: string;
  companyName?: string;
};

type WorkplaceMissedCall = {
  id: string;
  call_id: string;
  customer_phone: string;
  status: string;
  assigned_user_name?: string | null;
  contact_name?: string | null;
  company_name?: string | null;
  contact_id?: string | null;
  company_id?: string | null;
  ticket_id?: string | null;
  last_at: string;
  attempts: number;
  wait_seconds?: number;
};

type WorkplaceCallback = {
  id: string;
  phone: string;
  status: string;
  reason?: string;
  scheduled_at: string;
  contact_id?: string | null;
  company_id?: string | null;
  ticket_id?: string | null;
  contact_name?: string | null;
  company_name?: string | null;
  responsible_name?: string | null;
  attempts: number;
};

type WorkplaceDiagnostics = {
  settings?: { enabled?: boolean; mode?: string; secureTransport?: boolean };
  beelineSip?: { ready?: boolean; enabled?: boolean; lineCount?: number; sipUserId?: string; configurationStatus?: string };
  users?: { total?: number; enabled?: number; configured?: number; online?: number };
  calls?: { active?: number; missed_24h?: number; post_processing?: number };
  webhook?: { pending?: number; failed?: number; last_received_at?: string | null };
};

function compactDateTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function TelephonyWorkplacePanel({ onDial }: { onDial: (target: WorkplaceDialTarget) => void }) {
  const { user } = useAuth();
  const admin = userHasRole(user, 'ADMIN');
  const [missed, setMissed] = useState<WorkplaceMissedCall[]>([]);
  const [callbacks, setCallbacks] = useState<WorkplaceCallback[]>([]);
  const [diagnostics, setDiagnostics] = useState<WorkplaceDiagnostics | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingId, setPendingId] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const [missedRows, callbackRows, diagnosticRows] = await Promise.all([
        api<WorkplaceMissedCall[]>('/telephony/missed?status=NEW'),
        api<WorkplaceCallback[]>('/telephony/callbacks'),
        admin
          ? api<WorkplaceDiagnostics>('/telephony/browser/diagnostics')
          : Promise.resolve(null),
      ]);
      setMissed(missedRows);
      setCallbacks(callbackRows.filter(item => ['PLANNED', 'WAITING', 'IN_PROGRESS', 'POSTPONED', 'OVERDUE'].includes(item.status)));
      setDiagnostics(diagnosticRows);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Не удалось обновить рабочее место');
    } finally {
      setLoading(false);
    }
  }, [admin]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 30_000);
    const refresh = () => void load();
    window.addEventListener('intez:telephony-workplace-refresh', refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('intez:telephony-workplace-refresh', refresh);
    };
  }, [load]);

  const updateMissed = async (item: WorkplaceMissedCall, status: string, result = '') => {
    setPendingId(item.id);
    setError('');
    try {
      await api(`/telephony/missed/${item.id}`, { method: 'PATCH', body: JSON.stringify({ status, result }) });
      if (status === 'IN_PROGRESS') {
        onDial({
          phone: item.customer_phone,
          contactId: item.contact_id || undefined,
          companyId: item.company_id || undefined,
          ticketId: item.ticket_id || undefined,
          contactName: item.contact_name || 'Пропущенный звонок',
          companyName: item.company_name || undefined,
        });
      }
      await load();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : 'Не удалось обработать пропущенный звонок');
    } finally {
      setPendingId('');
    }
  };

  const updateCallback = async (item: WorkplaceCallback, status: string, result = '') => {
    setPendingId(item.id);
    setError('');
    try {
      await api(`/telephony/callbacks/${item.id}`, { method: 'PATCH', body: JSON.stringify({ status, result }) });
      if (status === 'IN_PROGRESS') {
        onDial({
          phone: item.phone,
          contactId: item.contact_id || undefined,
          companyId: item.company_id || undefined,
          ticketId: item.ticket_id || undefined,
          contactName: item.contact_name || 'Обратный звонок',
          companyName: item.company_name || undefined,
        });
      }
      await load();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : 'Не удалось обновить обратный звонок');
    } finally {
      setPendingId('');
    }
  };

  return <section className="telephony-workplace surface">
    <header className="telephony-workplace-head">
      <div><span><Headphones /></span><div><h2>Рабочее место оператора</h2><p>Пропущенные обращения, запланированные звонки и состояние браузерной телефонии</p></div></div>
      <button type="button" className="button ghost compact" onClick={() => void load()} disabled={loading}><RefreshCw className={loading ? 'spinning' : ''} /> Обновить</button>
    </header>
    {error && <div className="browser-phone-message error">{error}</div>}
    {diagnostics && <div className="telephony-workplace-health">
      <span className={diagnostics.beelineSip?.ready ? 'ok' : 'warn'}><PhoneCall /><b>{diagnostics.beelineSip?.ready ? 'SIP Билайн настроен' : 'SIP Билайн не настроен'}</b><small>{diagnostics.beelineSip?.lineCount || 0} одновременных линий · {diagnostics.beelineSip?.sipUserId || 'нет SIP User ID'}</small></span>
      <span className={diagnostics.settings?.enabled ? 'ok' : 'warn'}><Headphones /><b>{diagnostics.settings?.enabled ? 'WebRTC включён' : 'WebRTC выключен'}</b><small>{diagnostics.settings?.mode || 'EXTERNAL'} · {diagnostics.settings?.secureTransport ? 'защищённый WSS' : 'WSS не подтверждён'}</small></span>
      <span className={Number(diagnostics.users?.configured || 0) > 0 ? 'ok' : 'warn'}><Users /><b>{diagnostics.users?.online || 0} операторов онлайн</b><small>{diagnostics.users?.configured || 0} из {diagnostics.users?.total || 0} настроено</small></span>
      <span className={Number(diagnostics.webhook?.failed || 0) === 0 ? 'ok' : 'warn'}><Link2 /><b>{diagnostics.webhook?.pending || 0} событий в очереди</b><small>{diagnostics.webhook?.failed || 0} ошибок webhook</small></span>
      <span className={Number(diagnostics.calls?.active || 0) === 0 ? '' : 'active'}><PhoneCall /><b>{diagnostics.calls?.active || 0} активных звонков</b><small>{diagnostics.calls?.post_processing || 0} на постобработке</small></span>
    </div>}
    <div className="telephony-workplace-columns">
      <article className="telephony-workplace-column">
        <div className="telephony-workplace-column-head"><span><PhoneMissed /></span><div><h3>Пропущенные</h3><p>Требуют обязательной обработки</p></div><b>{missed.length}</b></div>
        <div className="telephony-workplace-list">
          {missed.slice(0, 6).map(item => <div className="telephony-workplace-item" key={item.id}>
            <span className="telephony-workplace-item-icon missed"><PhoneMissed /></span>
            <div className="telephony-workplace-item-copy"><b>{item.contact_name || item.customer_phone || 'Номер не определён'}</b><span>{item.company_name || 'Компания не определена'}</span><small>{compactDateTime(item.last_at)} · попыток: {item.attempts || 1}</small></div>
            <div className="telephony-workplace-item-actions"><button type="button" className="button primary compact" disabled={pendingId === item.id} onClick={() => void updateMissed(item, 'IN_PROGRESS')}><Phone /> Перезвонить</button><button type="button" className="icon-button" disabled={pendingId === item.id} onClick={() => void updateMissed(item, 'NOT_REQUIRED', 'Обработка закрыта оператором')} title="Не требует обработки"><X /></button></div>
          </div>)}
          {!loading && !missed.length && <div className="telephony-workplace-empty"><CheckCircle2 /><span><b>Необработанных звонков нет</b><small>Все пропущенные обращения разобраны</small></span></div>}
        </div>
      </article>
      <article className="telephony-workplace-column">
        <div className="telephony-workplace-column-head"><span><Clock3 /></span><div><h3>Обратные звонки</h3><p>Запланированные и просроченные</p></div><b>{callbacks.length}</b></div>
        <div className="telephony-workplace-list">
          {callbacks.slice(0, 6).map(item => <div className={`telephony-workplace-item ${new Date(item.scheduled_at).getTime() < Date.now() ? 'overdue' : ''}`} key={item.id}>
            <span className="telephony-workplace-item-icon callback"><Clock3 /></span>
            <div className="telephony-workplace-item-copy"><b>{item.contact_name || item.phone}</b><span>{item.reason || item.company_name || 'Обратный звонок'}</span><small>{compactDateTime(item.scheduled_at)} · {item.responsible_name || 'не назначен'}</small></div>
            <div className="telephony-workplace-item-actions"><button type="button" className="button primary compact" disabled={pendingId === item.id} onClick={() => void updateCallback(item, 'IN_PROGRESS')}><Phone /> Позвонить</button><button type="button" className="icon-button" disabled={pendingId === item.id} onClick={() => void updateCallback(item, 'COMPLETED', 'Выполнено оператором')} title="Отметить выполненным"><CheckCircle2 /></button></div>
          </div>)}
          {!loading && !callbacks.length && <div className="telephony-workplace-empty"><Clock3 /><span><b>Обратных звонков нет</b><small>Новые задачи появятся после постобработки звонков</small></span></div>}
        </div>
      </article>
    </div>
  </section>;
}
